// PURE board geometry of building overlays (M10), in world units around a cell centre: the mounted Gallery
// gun and its muzzle, the rail targets, the moving parts (drill bit, press piston, grinder wheel, smelter
// glow), the facing arrow, the progress bar (a machine facing S keeps its arrow on the bottom edge and the
// bar moves up above it: they never overlap), the stall badge and the contents indicators. BuildingViews
// draws with it and BoardFx places its effects with it, so an effect always starts where its sprite is drawn.
// Anchor points come from art/anchors.ts (one home with the generators that draw the features).
import { CASTER, CRATE, GALLERY, GUN, LOADER, SMELTER } from '../../art/anchors.ts';
import { DIGIT_ASPECT } from '../../art/catalog.ts';
import { MOVING_PARTS } from '../../art/machines/index.ts';
import type { FrameSpec } from '../../art/types.ts';
import type { Dir } from '../../sim/core/types.ts';

export interface Pt {
  x: number;
  y: number;
}

/** A placed sprite: centre, display size (world units) and clockwise angle in degrees. */
export interface Pose extends Pt {
  w: number;
  h: number;
  rot: number;
}

/** Clockwise rotation of an overlay drawn pointing N (the drill head / bit) toward each side. */
export const TURN_DEG: Readonly<Record<Dir, number>> = { N: 0, E: 90, S: 180, W: 270 };
export const DIR_X: Readonly<Record<Dir, number>> = { N: 0, E: 1, S: 0, W: -1 };
export const DIR_Y: Readonly<Record<Dir, number>> = { N: -1, E: 0, S: 1, W: 0 };

export interface GeoCfg {
  /** ui.json sprites.gunSize / gunLift (cells). */
  gunSize: number;
  gunLift: number;
  /** juice.json board.gunMountDeg: the mounted gun's turn; palette.json art.gunAngleDeg: the art's own. */
  gunMountDeg: number;
  gunArtDeg: number;
  /** ui.json render.arrowSize / barHeight, sprites.barWidth / barLift (cells; lift in bar heights). */
  arrowSize: number;
  barWidth: number;
  barHeight: number;
  barLift: number;
  /** juice.json board: rail target, contents icon, count digit height, pip and heat gauge sizes (cells). */
  targetSize: number;
  iconSize: number;
  digitH: number;
  pipSize: number;
  heatBarW: number;
  heatBarH: number;
  /** juice.json board.countMax: the largest contents count shown. */
  countMax: number;
}

/** Rotates (x, y) by `deg` clockwise (screen coordinates, y down). */
export function rotate(x: number, y: number, deg: number): Pt {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return { x: x * c - y * s, y: x * s + y * c };
}

/** A point given in cell units of the building frame (0–1 from its top-left) → world. */
export function cellPt(cx: number, cy: number, wu: number, u: number, v: number): Pt {
  return { x: cx + (u - 0.5) * wu, y: cy + (v - 0.5) * wu };
}

/** The mounted gun: above the stand, turned by gunMountDeg (it aims up at the rail targets). */
export function gunPose(cx: number, cy: number, wu: number, g: GeoCfg): Pose {
  const size = g.gunSize * wu;
  return {
    x: cx + (GALLERY.stand.x - 0.5) * wu,
    y: cy - g.gunLift * wu,
    w: size,
    h: size,
    rot: g.gunMountDeg,
  };
}

/** Screen angle of the barrel (0 = east, negative = up): the art's angle plus the mount's turn. */
export function barrelDeg(g: GeoCfg): number {
  return g.gunArtDeg + g.gunMountDeg;
}

/** World position of a point given in cell units of the gun FRAME (the muzzle socket centroid, the port). */
export function onGun(pose: Pose, u: number, v: number): Pt {
  const p = rotate((u - 0.5) * pose.w, (v - 0.5) * pose.h, pose.rot);
  return { x: pose.x + p.x, y: pose.y + p.y };
}

/** Where a spent casing leaves the mounted gun. */
export function gunPort(pose: Pose): Pt {
  return onGun(pose, GUN.port.x, GUN.port.y);
}

/** The standing rail target `i` (0–2): its base rests on the rail top. */
export function targetPose(cx: number, cy: number, wu: number, g: GeoCfg, i: number): Pose {
  const size = g.targetSize * wu;
  const px = GALLERY.pegX[i % GALLERY.pegX.length] ?? 0.5;
  return {
    x: cx + (px - 0.5) * wu,
    y: cy + (GALLERY.railY - 0.5) * wu - size / 2,
    w: size,
    h: size,
    rot: 0,
  };
}

export const TARGET_SLOTS = GALLERY.pegX.length;

export type MovingPart = 'bit' | 'piston' | 'wheel' | 'glow';

/** Centre + size of a moving part of `type` (MOVING_PARTS, drawn in its own frame), or null. */
export function partPose(
  type: string,
  part: MovingPart,
  cx: number,
  cy: number,
  wu: number,
  dir: Dir = 'N',
): Pose | null {
  const p = MOVING_PARTS.find((m) => m.type === type && m.part === part);
  if (!p) return null;
  const ox = p.ax + (0.5 - p.px) * p.w - 0.5;
  const oy = p.ay + (0.5 - p.py) * p.h - 0.5;
  // the drill bit belongs to the head: it turns with the drill's facing
  const turn = type === 'drill' ? TURN_DEG[dir] : 0;
  const o = rotate(ox * wu, oy * wu, turn);
  return { x: cx + o.x, y: cy + o.y, w: p.w * wu, h: p.h * wu, rot: turn };
}

/** The facing arrow on the output edge (inside the cell, `k` = the body's pop scale). */
export function arrowPose(cx: number, cy: number, wu: number, g: GeoCfg, dir: Dir, k = 1): Pose {
  const a = g.arrowSize * wu * k;
  const off = (0.5 - g.arrowSize / 2) * wu * k;
  return { x: cx + DIR_X[dir] * off, y: cy + DIR_Y[dir] * off, w: a, h: a, rot: 0 };
}

/**
 * The progress bar (centre origin; the fill is cropped from the left): `barLift` bar heights above the bottom
 * edge. A building with a SOUTH facing arrow lifts it above the arrow (half a bar of gap), so the bar and the
 * arrow never overlap (integration review).
 */
export function barPose(cx: number, cy: number, wu: number, g: GeoCfg, southArrow = false): Pose {
  const bw = g.barWidth * wu;
  const bh = g.barHeight * wu;
  const bottom = southArrow ? cy + (0.5 - g.arrowSize) * wu : cy + wu * 0.5;
  const lift = southArrow ? 1 : g.barLift;
  return { x: cx, y: bottom - bh * lift, w: bw, h: bh, rot: 0 };
}

/** The stall badge, top-right, `share` of the cell. */
export function stallPose(cx: number, cy: number, wu: number, share: number): Pose {
  const size = share * wu;
  return { x: cx + wu * 0.5 - size / 2, y: cy - wu * 0.5 + size / 2, w: size, h: size, rot: 0 };
}

/** Where the Gallery's queued-ammo icon sits (on the counter, left of the gun stand). */
export function galleryAmmoPose(cx: number, cy: number, wu: number, g: GeoCfg): Pose {
  const s = g.iconSize * wu;
  const p = cellPt(cx, cy, wu, GALLERY.counterIcon.x, GALLERY.counterIcon.y);
  return { ...p, w: s, h: s, rot: 0 };
}

/** The Gallery's queue dot `i` (the baked dots on its front strip; a filled one is overlaid). */
export function queueDotPose(cx: number, cy: number, wu: number, i: number): Pose {
  const q = GALLERY.queueDot;
  const p = cellPt(cx, cy, wu, q.x + i * q.dx, q.y);
  const d = q.r * 2 * wu;
  return { ...p, w: d, h: d, rot: 0 };
}

/** Queue dots baked into the Gallery's front strip (machines/structures.ts galleryParts). */
export const QUEUE_DOTS = GALLERY.queueDots;

/** A Loader / Gunsmith slot pip `i` at its funnel mouth. */
export function slotPipPose(cx: number, cy: number, wu: number, g: GeoCfg, i: number): Pose {
  const x = LOADER.funnelX[i % LOADER.funnelX.length] ?? 0.5;
  const p = cellPt(cx, cy, wu, x, LOADER.fillY);
  const d = g.pipSize * wu;
  return { ...p, w: d, h: d, rot: 0 };
}

/** The Crate's family icon (on its label). */
export function crateIconPose(cx: number, cy: number, wu: number, g: GeoCfg): Pose {
  const s = g.iconSize * wu;
  return { ...cellPt(cx, cy, wu, CRATE.label.x, CRATE.label.y), w: s, h: s, rot: 0 };
}

/** Where a count is centred on a building (cell units of the frame). */
export const COUNT_AT = {
  crate: CRATE.count,
  gallery: GALLERY.counterCount,
} as const;

/** Advance between count digits, as a share of the digit frame width (the ink strokes may touch). */
export const DIGIT_STEP = 0.9;

/** Digit `i` of an `n`-digit count centred on (u, v) of the cell. */
export function digitPose(
  cx: number,
  cy: number,
  wu: number,
  g: GeoCfg,
  u: number,
  v: number,
  i: number,
  n: number,
): Pose {
  const h = g.digitH * wu;
  const w = h * DIGIT_ASPECT;
  const step = w * DIGIT_STEP;
  const x0 = cx + (u - 0.5) * wu - ((n - 1) * step) / 2;
  return { x: x0 + i * step, y: cy + (v - 0.5) * wu, w, h, rot: 0 };
}

/** The Smelter's heat gauge (a bar frame turned upright: its left-cropped fill grows from the bottom). */
export function heatBarPose(cx: number, cy: number, wu: number, g: GeoCfg): Pose {
  const p = cellPt(cx, cy, wu, SMELTER.gauge.x, SMELTER.gauge.y);
  return { ...p, w: g.heatBarH * wu, h: g.heatBarW * wu, rot: -90 };
}

/** The Caster's pour glow (its crucible). */
export function casterPour(cx: number, cy: number, wu: number): Pt {
  return cellPt(cx, cy, wu, CASTER.pour.x, CASTER.pour.y);
}

/** The centroid of each gun frame's muzzle socket (catalog `sockets.muzzle`), by frame id. */
export function muzzlesOf(frames: readonly FrameSpec[]): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>();
  for (const f of frames) {
    if (f.gen !== 'item' || f.sockets === undefined || f.sockets.muzzle.length === 0) continue;
    let x = 0;
    let y = 0;
    for (const [px, py] of f.sockets.muzzle) {
      x += px;
      y += py;
    }
    const n = f.sockets.muzzle.length;
    out.set(f.key, { x: x / n, y: y / n });
  }
  return out;
}
